import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { IconButton, InputAdornment, TextField, TextFieldProps } from '@mui/material';
import { DatePickerSlotProps } from '@mui/x-date-pickers/DatePicker';
import { CalendarIcon, ClockIcon } from '@mui/x-date-pickers/icons';
import {
  usePickerAdapter,
  usePickerContext,
  usePickerTranslations,
} from '@mui/x-date-pickers/hooks';
import { IMask, useIMask } from 'react-imask';
import clsx from 'clsx';
import { mergeRef } from 'hooks/use-merge-ref';
import { objectSlotProps } from 'utils/slot-props';

/**
 * The text field the app's pickers render (Phase D2, x-date-pickers 9).
 *
 * x-date-pickers 9 removed the `<input>`-based field DOM
 * (`enableAccessibleFieldDOMStructure={false}`) that D1 relied on; its only
 * field is now the contenteditable sections widget (`PickersTextField`), which
 * has its own metrics, placeholders and separators and is not an `<input>`.
 * Passed as `slots.field`, this component keeps the pickers on a plain MUI
 * `TextField` -- the DOM the v3 keyboard pickers had and the pixel baselines
 * show -- with masked typing on the existing IMask pattern
 * (`components/app-input-masked`, `time-select`).
 *
 * Contract with the picker (all through the hooks x-date-pickers publishes):
 *  - `usePickerContext()` gives the value, the format, the open / disabled /
 *    read-only state, the label / name / className / sx of the picker and the
 *    `setValue` / `setOpen` actions; the popper anchors to `triggerRef`.
 *  - typing publishes on every accepted keystroke like the v3 keyboard field:
 *    an empty field is `null`, an incomplete or unparsable one is an invalid
 *    Date, a complete one is the parsed date -- always as a plain change
 *    (`changeImportance: 'set'`), never as an accept (R-19). The adapter
 *    parses and formats, so the field honours the picker's `format` and
 *    locale. An invalid value coming back from the consumer never touches the
 *    text -- it is the user's own input. The error state and the built-in
 *    helper text are the wrapper's (`validation.ts`, the v3 rules).
 *  - the calendar / clock button is the same `IconButton` + `InputAdornment`
 *    the built-in field renders (`slotProps.openPickerButton`,
 *    `slots.openPickerIcon`); an `endAdornment` given through the text-field
 *    slot replaces it, as in MUI's field.
 *  - the text-field props travel in the picker's `slotProps.textField` (the
 *    picker hands its whole `slotProps` to the field). Object form only.
 */
type TextFieldSlotProps = NonNullable<DatePickerSlotProps['textField']>;
type OpenPickerButtonSlotProps = NonNullable<DatePickerSlotProps['openPickerButton']>;

/**
 * Types TextField props as a picker's `slotProps.textField`. MUI types that
 * slot for its own PickersTextField; AppPickerField reads it back as
 * TextFieldProps (below), so the two casts are each other's inverse and the
 * wrappers can type their consumers' `slotProps.textField` as TextFieldProps.
 */
export const asPickerTextFieldSlotProps = (props: TextFieldProps): TextFieldSlotProps =>
  props as unknown as TextFieldSlotProps;

export interface AppPickerFieldProps {
  id?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  slots?: { openPickerIcon?: React.ElementType };
  slotProps?: { textField?: TextFieldSlotProps; openPickerButton?: OpenPickerButtonSlotProps };
}

/**
 * date-fns tokens the app's formats use, as IMask blocks (24-hour formats:
 * `APP_FORMAT_DATE`, `APP_FORMAT_TIME`, `APP_FORMAT_DATE_TIME`; a 12-hour
 * `a` token would fall back to IMask's plain letter definition).
 */
const MASK_BLOCKS: Record<string, IMask.AnyMaskedOptions> = {
  dd: { mask: IMask.MaskedRange, from: 1, to: 31, maxLength: 2, autofix: 'pad' },
  MM: { mask: IMask.MaskedRange, from: 1, to: 12, maxLength: 2, autofix: 'pad' },
  yyyy: { mask: IMask.MaskedRange, from: 1000, to: 9999, maxLength: 4 },
  HH: { mask: IMask.MaskedRange, from: 0, to: 23, maxLength: 2, autofix: 'pad' },
  hh: { mask: IMask.MaskedRange, from: 1, to: 12, maxLength: 2, autofix: 'pad' },
  mm: { mask: IMask.MaskedRange, from: 0, to: 59, maxLength: 2, autofix: 'pad' },
  ss: { mask: IMask.MaskedRange, from: 0, to: 59, maxLength: 2, autofix: 'pad' },
};

const isTimeOnlyFormat = (format: string) => !/[dMy]/.test(format);

export const AppPickerField = ({ id, inputRef, slots, slotProps }: AppPickerFieldProps) => {
  const adapter = usePickerAdapter();
  const translations = usePickerTranslations();
  const picker = usePickerContext();
  const { value, fieldFormat: format } = picker;

  // MUI types `slotProps.textField` for its PickersTextField; the keys the
  // app's wrappers pass (variant, error, helperText, size, fullWidth, required,
  // style, className, onBlur, slotProps.{input,inputLabel,htmlInput,formHelperText})
  // mean the same on TextField.
  const textField = (objectSlotProps(slotProps?.textField) ??
    {}) as unknown as Partial<TextFieldProps>;
  const { slotProps: textFieldSlotProps, ...textFieldRest } = textField;
  const inputSlotProps = objectSlotProps(textFieldSlotProps?.input);
  const openPickerButton = objectSlotProps(slotProps?.openPickerButton);

  const isTimeOnly = isTimeOnlyFormat(format);
  const validDate = value !== null && adapter.isValid(value) ? value : null;
  const formatted = validDate ? adapter.formatByString(validDate, format) : null;

  const maskOptions = useMemo<IMask.AnyMaskedOptions>(() => {
    const blocks: Record<string, IMask.AnyMaskedOptions> = {};
    Object.keys(MASK_BLOCKS).forEach((token) => {
      if (format.includes(token)) {
        blocks[token] = MASK_BLOCKS[token];
      }
    });
    return { mask: format, blocks, lazy: true };
  }, [format]);

  // The text last written into the mask from the picker's value: the mask's
  // `accept` for it (and for the text it finds at mount) is not a user edit.
  const synced = useRef(formatted ?? '');
  const publish = useRef<(text: string, complete: boolean) => void>(() => undefined);
  publish.current = (text, complete) => {
    if (text === synced.current) {
      return;
    }
    // `changeImportance: 'set'`: a keystroke is a change, never an accept
    // (the default importance would fire the picker's `onAccept` per
    // keystroke; v3's keyboard field only ever published `onChange`, R-19).
    if (text === '') {
      picker.setValue(null, { source: 'field', changeImportance: 'set', shouldClose: false });
      return;
    }
    const parsed = complete ? adapter.parse(text, format) : null;
    picker.setValue(parsed && adapter.isValid(parsed) ? parsed : new Date(NaN), {
      source: 'field',
      changeImportance: 'set',
      shouldClose: false,
    });
  };
  const onAccept = useCallback(
    (text: string, mask: IMask.InputMask<IMask.AnyMaskedOptions>) =>
      publish.current(text, mask.masked.isComplete),
    [],
  );
  const { ref: maskInputRef, maskRef, value: maskText } = useIMask(maskOptions, { onAccept });
  // Until the mask exists (it attaches after the first commit) the input shows
  // the picker's value itself, so a filled field never paints empty.
  const text = maskRef.current ? maskText : formatted ?? '';

  const { open } = picker;
  useEffect(() => {
    const mask = maskRef.current;
    // While the modal is open the picker's value is the dialog's draft
    // (`components/app-picker-modal`); the field keeps showing the committed
    // text, as v3's did, and syncs when the dialog closes on OK or Cancel.
    if (!mask || open) {
      return;
    }
    // An invalid date is the user's own incomplete input: leave the text alone.
    const next = value === null ? '' : formatted;
    if (next === null) {
      return;
    }
    synced.current = next;
    if (mask.value !== next) {
      mask.value = next;
    }
  }, [value, formatted, maskRef, open]);

  // Memoised: `triggerRef` is a state setter in the picker, so a fresh callback
  // ref every render would detach / re-attach it (two setState calls) per render.
  const { rootRef, triggerRef } = picker;
  const inputSlotRef = (inputSlotProps?.ref as React.Ref<HTMLElement> | undefined) ?? null;
  const handleInputRef = useMemo(
    () => mergeRef<HTMLInputElement>(maskInputRef, inputRef ?? null),
    [maskInputRef, inputRef],
  );
  const handleRootRef = useMemo(() => mergeRef<HTMLDivElement>(rootRef ?? null), [rootRef]);
  const handleInputRootRef = useMemo(
    () => mergeRef<HTMLElement>(inputSlotRef, triggerRef),
    [inputSlotRef, triggerRef],
  );

  const openPickerAriaLabel = isTimeOnly
    ? translations.openTimePickerDialogue(
        validDate ? adapter.format(validDate, 'fullTime24h') : null,
      )
    : translations.openDatePickerDialogue(validDate ? adapter.format(validDate, 'fullDate') : null);
  const OpenPickerIcon = slots?.openPickerIcon ?? (isTimeOnly ? ClockIcon : CalendarIcon);
  const openPickerAdornment =
    picker.triggerStatus === 'hidden' ? null : (
      <InputAdornment position="end">
        <IconButton
          edge={false}
          {...openPickerButton}
          disabled={picker.triggerStatus === 'disabled'}
          onClick={(event) => {
            event.preventDefault();
            picker.setOpen(true);
          }}
          aria-label={openPickerAriaLabel}
          data-mui-picker-open-button="true"
        >
          <OpenPickerIcon />
        </IconButton>
      </InputAdornment>
    );

  return (
    <TextField
      {...textFieldRest}
      id={id ?? textFieldRest.id}
      ref={handleRootRef}
      inputRef={handleInputRef}
      value={text}
      // IMask owns the input's text (its `accept` publishes to the picker);
      // the controlled value only keeps the label shrink logic in step.
      onChange={() => undefined}
      label={picker.label}
      name={picker.name}
      className={clsx(picker.rootClassName, textFieldRest.className) || undefined}
      sx={picker.rootSx}
      disabled={picker.disabled}
      autoFocus={picker.autoFocus && !picker.open}
      focused={picker.open ? true : undefined}
      // The error state is the wrapper's (its consumer's `error` or the v3
      // built-in validation, `validation.ts`): the picker's own validity would
      // also count `shouldDisableDate`, which v3 never painted red (R-21).
      error={Boolean(textFieldRest.error)}
      slotProps={{
        ...textFieldSlotProps,
        input: {
          ...inputSlotProps,
          ref: handleInputRootRef,
          readOnly: picker.readOnly || Boolean(inputSlotProps?.readOnly),
          endAdornment: inputSlotProps?.endAdornment ?? openPickerAdornment,
        },
      }}
    />
  );
};
